/**
 * The house way to export a PDF: show it first, download on request.
 *
 * The endpoints are Bearer-authenticated, so a tab cannot simply navigate to the URL —
 * the PDF has to be fetched with axios and handed over as a blob. Never `window.open()`
 * an `/api/...` PDF url directly: the tab carries no token and gets a 401.
 *
 * Buttons should use `<pdf-button>` (resources/js/components/PdfButton.vue), which wires
 * both functions below into a split button.
 */

/**
 * The file name the server gave the PDF (Content-Disposition), when it gave one.
 *
 * @param {Object} headers
 * @returns {String|null}
 */
export function serverFilename(headers) {
    const disposition = headers?.['content-disposition'];
    const match = disposition?.match(/filename[^;=\n]*=["']?([^"';\n]+)/);

    return match ? match[1] : null;
}

/**
 * Fetch a PDF and wrap it in a blob the browser renders instead of downloading.
 *
 * @param {String} url
 * @param {Object} params
 * @param {Object} config
 * @returns {Promise<{objectUrl: String, filename: String|null}>} the caller revokes the url
 */
async function fetchPdf(url, params, config) {
    const response = await axios.get(url, { ...config, params, responseType: 'blob' });

    return {
        // Explicit type: a Blob rebuilt without it defaults to octet-stream, which browsers
        // download instead of rendering.
        objectUrl: URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })),
        filename: serverFilename(response.headers)
    };
}

function saveAs(objectUrl, filename) {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
}

/**
 * Fetch a PDF from the API and show it in a new browser tab.
 *
 * @param {String} url    Absolute API url of the PDF endpoint
 * @param {Object} params Query parameters
 * @param {String} filename Name used if the tab is blocked and we fall back to a download,
 *                          when the server names none
 * @param {Object} config Extra axios config — the portal passes its Bearer header here
 * @returns {Promise<Boolean>} true once the PDF has been handed to a tab or downloaded
 */
export default async function openPdf(url, params = {}, filename = 'report.pdf', config = {}) {
    // Opened before the request, while we are still inside the click handler: a tab opened
    // after an await reads as an unsolicited popup and gets blocked.
    const tab = window.open('', '_blank');

    try {
        const pdf = await fetchPdf(url, params, config);
        const objectUrl = pdf.objectUrl;

        if (tab && !tab.closed) {
            tab.location = objectUrl;
        } else {
            // Popup blocked — fall back to a download rather than losing the export
            saveAs(objectUrl, pdf.filename || filename);
        }

        // Revoked late: releasing it right away would blank the tab that just opened it.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);

        return true;
    } catch (error) {
        if (tab && !tab.closed) {
            tab.close();
        }
        throw error;
    }
}

/**
 * Fetch a PDF from the API and save it straight to disk, skipping the viewer.
 *
 * @param {String} url
 * @param {Object} params
 * @param {String} filename
 * @param {Object} config
 * @returns {Promise<Boolean>}
 */
export async function downloadPdf(url, params = {}, filename = 'report.pdf', config = {}) {
    const { objectUrl, filename: named } = await fetchPdf(url, params, config);
    saveAs(objectUrl, named || filename);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);

    return true;
}
